import { CafDecisionRow, CafImmutableTrace } from '../../../api/cafCorpus';
import { ACTION_COPY, HoldGroupKey, VERDICT_COPY, actionCopy, holdGroup, holdLabel, holdSentence, ruleSentence } from '../copy';
import { IconName } from '../Icon';

export const happenedSentence = (row: CafDecisionRow): string => {
  if (row.archived) return 'Archived.';
  const claim = row.assigned_claim_id;
  if (row.completed && claim) return `Attached to ${claim} and marked done.`;
  if (row.completed) return 'Marked done.';
  if (claim) return `Attached to ${claim}. Waiting in the Lender lane for a person.`;
  return 'Waiting in the Lender lane for a person. It was not attached to a claim.';
};

export interface StaffCheckView { label: string; className: string; icon: IconName | null }

export const staffCheckView = (row: CafDecisionRow, enriched: boolean): StaffCheckView => {
  if (row.overridden_at) return { label: 'Fixed by staff', className: 'cur-v-fixed', icon: 'wrench' };
  if (!enriched) return { label: '—', className: 'cur-v-none', icon: null };
  if (row.staff_check) {
    const verdict = VERDICT_COPY[row.staff_check];
    return { label: verdict.label, className: verdict.className, icon: verdict.icon };
  }
  return { label: 'Not checked', className: 'cur-v-none', icon: null };
};

/** Only when reasons are available and the decision's attempt has been recorded (never a guess). */
export const reasonLine = (row: CafDecisionRow, reasons: boolean): string | null =>
  reasons && row.action === 'MANUAL_REVIEW' && row.decision_id ? holdLabel(row.hold_reason) : null;

export type DecisionFilter = 'all' | 'staff' | 'wrong' | 'unchecked';

export const matchesFilter = (row: CafDecisionRow, filter: DecisionFilter, enriched: boolean): boolean => {
  if (filter === 'staff') return row.action === 'MANUAL_REVIEW';
  if (filter === 'wrong') return enriched && row.staff_check === 'incorrect';
  if (filter === 'unchecked') return enriched && !row.staff_check && !row.overridden_at;
  return true;
};

export const matchesReason = (row: CafDecisionRow, key: HoldGroupKey, reasons: boolean): boolean =>
  reasons && row.action === 'MANUAL_REVIEW' && Boolean(row.decision_id) && holdGroup(row.hold_reason) === key;

export interface AiStory {
  did: string;
  action: string | null;
  /** Whole percent, or null when unknown. */
  sure: number | null;
  said: string | null;
  change: { from: string; to: string; because: string } | null;
  rule: string | null;
}

/** Some rule effects record other values in before/after (a claim reference, a category). */
const isActionCode = (value: unknown): value is string =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(ACTION_COPY, value);

/**
 * `did`/`change.to` use `actionCopy(...)`, so MANUAL_REVIEW reads "Marked as manual" /
 * "mark as manual" (Ryan's ruling) automatically -- copy.ts is the single source, never
 * hardcoded here.
 */
export const aiStory = (row: CafDecisionRow, trace?: CafImmutableTrace | null): AiStory => {
  const effective = (trace?.effective_decision ?? {}) as Record<string, unknown>;
  const action = typeof effective.action === 'string' ? effective.action : row.action;
  const confidence = typeof effective.confidence === 'number' ? effective.confidence : row.confidence;
  const said = typeof effective.reasoning === 'string' ? effective.reasoning : row.reasoning;
  const hold = typeof effective.hold_reason === 'string' ? effective.hold_reason : row.hold_reason ?? null;
  const effects = (trace?.rule_effects ?? []) as Array<Record<string, unknown>>;
  // Only an action change is a "suggested X, changed to Y" story; identity and classification
  // checks (current_message_identity, category_override) change other values.
  const first = effects.find((effect) => isActionCode(effect.before) && isActionCode(effect.after) && effect.before !== effect.after);
  const last = [...effects].reverse().find((effect) => effect.after === action) ?? first;
  const change = first && action && first.before !== action
    ? { from: actionCopy(first.before as string).label.toLowerCase(), to: actionCopy(action).label.toLowerCase(), because: ruleSentence(last ?? first) }
    : null;
  const rule = !change && action === 'MANUAL_REVIEW' && (hold || trace) ? holdSentence(hold) : null;
  return {
    did: actionCopy(action).did,
    action,
    sure: typeof confidence === 'number' ? Math.round(confidence * 100) : null,
    said: said || null,
    change,
    rule,
  };
};
